import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { AiOutlineClose } from 'react-icons/ai';
import { FaSearch } from 'react-icons/fa';

import { injectIntl, intlShape } from '../../util/reactIntl';
import { ProfileSettingsModal } from '../../components';
import config from '../../config';

import css from './ProfileSettingsHobbiesForm.css';

const ProfileSettingsHobbiesForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    hobbies,
    hobbyOptions,
    updateInProgress,
    intl,
  } = props;

  const locale = config.locale;

  const [selectedKeys, setSelectedKeys] = useState(hobbies || []);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when the modal opens. Only on open: the page passes a freshly
  // mapped hobbies array on every render (resize, any Redux update), and
  // resetting then would drop the unsaved changes.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedKeys(hobbies || []);
      setSearchQuery('');
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, hobbies]);

  const isDirty =
    JSON.stringify([...selectedKeys].sort()) !== JSON.stringify([...(hobbies || [])].sort());

  const isSearching = searchQuery.trim().length > 0;

  const getLabel = key => {
    const opt = (hobbyOptions || []).find(o => o.key === key);
    return opt ? intl.formatMessage({ id: opt.label.props.id }) : key;
  };

  const visibleHobbies = useMemo(() => {
    const notSelected = (hobbyOptions || []).filter(o => !selectedKeys.includes(o.key));
    const withLabel = notSelected.map(o => ({
      key: o.key,
      label: intl.formatMessage({ id: o.label.props.id }),
    }));
    const filtered = isSearching
      ? withLabel.filter(o => o.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))
      : withLabel;
    return filtered.sort((a, b) =>
      a.label.localeCompare(b.label, locale, { sensitivity: 'base' })
    );
  }, [hobbyOptions, selectedKeys, isSearching, searchQuery, locale, intl]);

  const addHobby = key => {
    if (selectedKeys.includes(key)) return;
    setSelectedKeys([...selectedKeys, key]);
  };

  const removeHobby = key => {
    setSelectedKeys(selectedKeys.filter(k => k !== key));
  };

  const handleSave = () => {
    const hobbiesData = selectedKeys.map(key => ({ category: key }));
    onSubmit({ hobbies: hobbiesData });
    onClose();
  };

  return (
    <ProfileSettingsModal
      id="hobbies"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      title={intl.formatMessage({ id: 'ProfileSettingsPage.editHobbiesTitle' })}
      subtitle={intl.formatMessage({ id: 'ProfileSettingsPage.editHobbiesSubtitle' })}
      onSubmit={handleSave}
      submitDisabled={!isDirty}
      submitInProgress={updateInProgress}
      submitLabel={intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' })}
    >
      <div className={css.formContent}>
        <div className={css.counterRow}>
          <span className={css.counterLabel}>
            {selectedKeys.length > 0
              ? intl.formatMessage({ id: 'ProfileSettingsPage.yourHobbiesLabel' })
              : intl.formatMessage({ id: 'ProfileSettingsPage.hobbiesLabel' })}{' '}
            <span className={css.counterCount}>{selectedKeys.length}</span>
          </span>
        </div>

        {selectedKeys.length > 0 ? (
          <div className={css.selectedBox}>
            {selectedKeys.map(key => (
              <span key={key} className={css.hobbyTag}>
                {getLabel(key)}
                <button
                  type="button"
                  className={css.hobbyTagRemove}
                  onClick={() => removeHobby(key)}
                >
                  <AiOutlineClose className={css.hobbyTagRemoveIcon} />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div className={css.pickerBox}>
          <div className={css.searchRow}>
            <FaSearch className={css.searchIcon} />
            <input
              type="text"
              className={css.searchInput}
              placeholder={intl.formatMessage({
                id: 'ProfileSettingsPage.hobbiesSearchPlaceholder',
              })}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div className={css.hobbyGrid}>
            {visibleHobbies.length === 0 ? (
              <p className={css.emptyState}>
                {intl.formatMessage({ id: 'ProfileSettingsPage.hobbiesNoResults' })}
              </p>
            ) : (
              visibleHobbies.map(o => (
                <button
                  type="button"
                  key={o.key}
                  className={css.hobbyChip}
                  onClick={() => addHobby(o.key)}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </ProfileSettingsModal>
  );
};

ProfileSettingsHobbiesForm.defaultProps = {
  hobbies: [],
  hobbyOptions: [],
  updateInProgress: false,
};

ProfileSettingsHobbiesForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  hobbies: PropTypes.array,
  hobbyOptions: PropTypes.array,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsHobbiesForm);
